import { useState, useEffect, useRef } from 'react';

const WebdesignProcess = () => {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const steps = [
    {
      number: '01',
      title: 'Beratung & Konzept',
      description: 'Wir analysieren Ihre Anforderungen und entwickeln ein maßgeschneidertes Konzept für Ihre Website.',
      features: ['Zielgruppenanalyse', 'Wettbewerbsanalyse', 'Sitemap-Erstellung', 'Wireframes'],
      color: '#d1bc8a'
    },
    {
      number: '02',
      title: 'Design & Prototyping',
      description: 'Wir erstellen moderne Designs und interaktive Prototypen für Ihre Freigabe.',
      features: ['UI/UX Design', 'Responsive Layouts', 'Interaktive Prototypen', 'Design-System'],
      color: '#d1bc8a'
    },
    {
      number: '03',
      title: 'Entwicklung',
      description: 'Technische Umsetzung mit modernsten Technologien und höchsten Qualitätsstandards.',
      features: ['Frontend Development', 'Backend Integration', 'CMS-Anbindung', 'Performance-Optimierung'],
      color: '#d1bc8a'
    },
    {
      number: '04',
      title: 'Testing & Launch',
      description: 'Umfassende Tests und erfolgreicher Launch Ihrer neuen Website.',
      features: ['Cross-Browser Testing', 'Mobile Testing', 'SEO-Optimierung', 'Go-Live Support'],
      color: '#d1bc8a'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const section = sectionRef.current;
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      if (scrollPosition >= sectionTop && scrollPosition <= sectionTop + sectionHeight) {
        const relativeScroll = scrollPosition - sectionTop;
        const stepHeight = sectionHeight / steps.length;
        const newActiveStep = Math.min(
          Math.floor(relativeScroll / stepHeight),
          steps.length - 1
        );
        setActiveStep(Math.max(0, newActiveStep));
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [steps.length]);

  return (
    <section ref={sectionRef} className="py-32 bg-zinc-950 relative overflow-hidden">
      {/* Ambient Background Light */}
      <div 
        className="absolute inset-0 transition-all duration-1000 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% ${(activeStep / (steps.length - 1)) * 100}%, ${steps[activeStep].color}15 0%, transparent 50%)`
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-2 h-2 bg-[#d1bc8a] rounded-full"></div>
            <span className="text-sm uppercase tracking-[0.3em] text-gray-500 font-light">Prozess</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Unser Webdesign<br />Prozess
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto font-light">
            Von der ersten Idee bis zum erfolgreichen Launch - transparent und strukturiert
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative transition-all duration-500 cursor-pointer ${
                activeStep === index ? 'scale-100' : 'scale-95 opacity-60'
              }`}
              onClick={() => setActiveStep(index)}
            >
              {/* Step Card */}
              <div 
                className={`bg-zinc-900 border transition-all duration-500 ${
                  activeStep === index 
                    ? 'border-[#d1bc8a] shadow-[0_0_50px_rgba(209,188,138,0.3)]' 
                    : 'border-zinc-800'
                }`}
              >
                {/* Header */}
                <div className="p-8 flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <span className={`text-6xl font-bold transition-colors duration-500 ${
                      activeStep === index ? 'text-[#d1bc8a]' : 'text-zinc-700'
                    }`}>
                      {step.number}
                    </span>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">
                        {step.title}
                      </h3>
                      <p className="text-gray-400 font-light">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  <i className={`ri-arrow-${activeStep === index ? 'up' : 'down'}-s-line text-3xl text-gray-500 transition-transform duration-300`}></i>
                </div>

                {/* Progress Bar */}
                {activeStep === index && (
                  <div className="h-0.5 bg-zinc-800 relative overflow-hidden">
                    <div 
                      className="absolute inset-y-0 left-0 bg-[#d1bc8a]"
                      style={{
                        width: '100%',
                        animation: 'progressBar 3s ease-in-out infinite'
                      }}
                    />
                  </div>
                )}

                {/* Expanded Content */}
                <div 
                  className={`overflow-hidden transition-all duration-500 ${
                    activeStep === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="p-8 pt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {step.features.map((feature, featureIndex) => (
                      <div 
                        key={featureIndex}
                        className="bg-black border border-zinc-800 p-4 hover:border-[#d1bc8a] transition-colors duration-300"
                      >
                        <div className="w-1 h-1 bg-[#d1bc8a] rounded-full mb-3"></div>
                        <p className="text-sm text-gray-300 font-light">{feature}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes progressBar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </section>
  );
};

export default WebdesignProcess;